import React, { useState, useEffect } from 'react';
import { MapPin, Filter, Plus, Star, List, Map, CheckCircle, Users, Heart, Clock, Lock } from 'lucide-react';
import LoadingSpinner from '../components/LoadingSpinner';
import LoadingSkeleton from '../components/LoadingSkeleton';
import VenueSubmissionModal from '../components/VenueSubmissionModal';
import HelpButton from '../components/HelpButton';
import FavoriteButton from '../components/FavoriteButton';
import FavoritesPanel from '../components/FavoritesPanel';
import InteractiveMap from '../components/InteractiveMap';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const MapPage = () => {
  const { isAuthenticated, demoMode } = useAuth();
  const canContribute = isAuthenticated && !demoMode; // Only real users can add venues
  
  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedVenue, setSelectedVenue] = useState(null);
  const [showAddVenue, setShowAddVenue] = useState(false);
  const [locationSearch, setLocationSearch] = useState('');
  const [currentLocation, setCurrentLocation] = useState(null);
  const [locationLoading, setLocationLoading] = useState(false);
  const [searchRadius, setSearchRadius] = useState(25);
  const [mapCenter, setMapCenter] = useState({ lat: 45.3838, lng: -92.8490 }); // Default: Chisago City, MN
  const [showSubmissionModal, setShowSubmissionModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [viewMode, setViewMode] = useState('list'); // 'map' or 'list'
  const [showFavoritesPanel, setShowFavoritesPanel] = useState(false);

  // Helper function to filter out blank/invalid venues
  const filterValidVenues = (venueList) => {
    return venueList.filter(venue => {
      const name = venue.name?.trim();
      const address = venue.address?.trim();
      
      // Skip venues with no name or generic names
      if (!name || name === 'Unnamed Location' || name === 'unnamed' || name.length < 2) {
        return false;
      }
      
      // Skip venues with no address (unless they have good coordinates)
      if (!address || address === 'Address not available' || address.length < 3) {
        // Allow if venue has coordinates and good wheelchair data
        if (venue.latitude && venue.longitude && venue.wheelchair_accessibility === 'yes') {
          return true;
        }
        return false;
      }
      
      return true;
    });
  };

  // Calculate distance between two coordinates (Haversine formula)
  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 3959; // Earth's radius in miles
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
      Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  };

  // Sort venues by distance from a center point
  const sortVenuesByDistance = (venueList, centerLat, centerLng) => {
    return venueList.map(venue => ({
      ...venue,
      distance: calculateDistance(centerLat, centerLng, venue.latitude, venue.longitude)
    })).sort((a, b) => a.distance - b.distance);
  };

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Load initial venues
  useEffect(() => {
    loadSampleVenues();
    setLoading(false);
  }, []);

  // Get user's current location
  const getCurrentLocation = async () => {
    setLocationLoading(true);
    try {
      if (!navigator.geolocation) {
        throw new Error('Geolocation is not supported by this browser');
      }

      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 300000 // 5 minutes
        });
      });

      const location = {
        lat: position.coords.latitude,
        lng: position.coords.longitude
      };
      
      setCurrentLocation(location);
      setMapCenter(location);
      await searchVenuesByLocation(location.lat, location.lng, searchRadius);
      
    } catch (error) {
      console.error('Error getting location:', error);
      
      // Provide more specific error messages
      let errorMessage = 'Unable to get your location. ';
      if (error.code === 1) {
        errorMessage += 'Location access was denied. Please allow location access in your browser settings and try again.';
      } else if (error.code === 2) {
        errorMessage += 'Location information is unavailable. Please try searching by zip code instead.';
      } else if (error.code === 3) {
        errorMessage += 'Location request timed out. Please try again or search by zip code.';
      } else {
        errorMessage += 'Please check your browser settings or search by zip code.';
      }
      
      alert(errorMessage);
    }
    setLocationLoading(false);
  };

  // Search venues by location
  const searchVenuesByLocation = async (lat, lng, radius) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/venues/search`, {
        params: { latitude: lat, longitude: lng, radius_miles: radius }
      });
      // Filter out invalid/blank venues and sort by distance
      const validVenues = filterValidVenues(response.data);
      const sortedVenues = sortVenuesByDistance(validVenues, lat, lng);
      setVenues(sortedVenues);
    } catch (error) {
      console.error('Error searching venues:', error);
      // Fallback to sample data for demo
      loadSampleVenues();
    }
    setLoading(false);
  };

  // Search venues by zip code using nationwide search
  const searchVenuesByZipCode = async (zipCode) => {
    setLoading(true);
    try {
      // First, try the real OpenStreetMap accessibility API for Minnesota
      try {
        const realResponse = await axios.get(`${API_URL}/api/accessibility/venues/search`, {
          params: { zip_code: zipCode, radius_miles: searchRadius }
        });
        
        if (realResponse.data && realResponse.data.venues && realResponse.data.venues.length > 0) {
          // Transform the OSM data to match our venue format
          const transformedVenues = realResponse.data.venues.map(venue => ({
            id: venue.id,
            name: venue.name,
            address: `${venue.address}${venue.city ? ', ' + venue.city : ''}${venue.state ? ', ' + venue.state : ''} ${venue.zip_code}`.trim(),
            latitude: venue.latitude,
            longitude: venue.longitude,
            category: venue.category,
            description: venue.description,
            accessibility_features: venue.accessibility_features || ['wheelchair_accessible'],
            rating: venue.wheelchair_accessibility === 'yes' ? 4.5 : (venue.wheelchair_accessibility === 'limited' ? 3.5 : 3.0),
            review_count: 0,
            wheelchair_accessibility: venue.wheelchair_accessibility,
            phone: venue.phone,
            website: venue.website,
            source: 'OpenStreetMap',
            last_updated: venue.last_updated || new Date().toISOString()
          }));
          
          // Filter out invalid venues and sort by distance
          const validVenues = filterValidVenues(transformedVenues);
          const centerLat = realResponse.data.search_params?.center_lat || validVenues[0]?.latitude;
          const centerLng = realResponse.data.search_params?.center_lon || validVenues[0]?.longitude;
          const sortedVenues = sortVenuesByDistance(validVenues, centerLat, centerLng);
          setVenues(sortedVenues);
          
          // Update map center
          if (validVenues.length > 0) {
            setMapCenter({ lat: validVenues[0].latitude, lng: validVenues[0].longitude });
          } else if (realResponse.data.search_params) {
            setMapCenter({ 
              lat: realResponse.data.search_params.center_lat, 
              lng: realResponse.data.search_params.center_lon 
            });
          }
          
          setSuccessMessage(`Found ${validVenues.length} accessible venues from OpenStreetMap`);
          setTimeout(() => setSuccessMessage(''), 3000);
          setLoading(false);
          return;
        }
      } catch (osmError) {
        console.log('OSM API not available, falling back to database:', osmError.message);
      }
      
      // Fallback to the existing database search
      const response = await axios.get(`${API_URL}/api/venues/nationwide-search`, {
        params: { zip_code: zipCode }
      });
      
      if (response.data && response.data.venues) {
        // Filter out invalid venues
        const validVenues = filterValidVenues(response.data.venues);
        setVenues(validVenues);
        
        // If venues found, update map center to the first venue's location
        if (validVenues.length > 0) {
          const firstVenue = validVenues[0];
          setMapCenter({ lat: firstVenue.latitude, lng: firstVenue.longitude });
        }
      } else {
        setVenues([]);
        alert(`No accessible venues found in zip code ${zipCode}. Try a different zip code or increase the search radius.`);
      }
    } catch (error) {
      console.error('Error searching venues by zip code:', error);
      setVenues([]);
      alert('Error searching venues by zip code. Please try again.');
    }
    setLoading(false);
  };

  // Search by zip code or city/state
  const handleLocationSearch = async () => {
    if (!locationSearch.trim()) return;
    
    try {
      // Check if input is a zip code (5 digits)
      const zipCodeRegex = /^\d{5}$/;
      
      if (zipCodeRegex.test(locationSearch)) {
        // Use nationwide search endpoint for zip code searches
        await searchVenuesByZipCode(locationSearch);
      } else {
        // For city/state searches, use a basic geocoding approach
        setLoading(true);
        const searchLocation = await getCoordinatesFromCityState(locationSearch);
        if (searchLocation) {
          setMapCenter(searchLocation);
          await searchVenuesByLocation(searchLocation.lat, searchLocation.lng, searchRadius);
        } else {
          alert('Location not found. Please try a valid zip code or city, state format.');
          loadSampleVenues();
        }
        setLoading(false);
      }
    } catch (error) {
      console.error('Error searching location:', error);
      alert('Error searching location. Please try again.');
      loadSampleVenues();
      setLoading(false);
    }
  };

  // Basic zip code to coordinates lookup (sample of major cities)
  const getCoordinatesFromZipCode = async (zipCode) => {
    const zipCoordinates = {
      // Major cities across US
      '10001': { lat: 40.7505, lng: -73.9934 }, // New York, NY
      '90210': { lat: 34.0901, lng: -118.4065 }, // Beverly Hills, CA
      '60601': { lat: 41.8825, lng: -87.6441 }, // Chicago, IL
      '77001': { lat: 29.7604, lng: -95.3698 }, // Houston, TX
      '85001': { lat: 33.4484, lng: -112.0740 }, // Phoenix, AZ
      '19101': { lat: 39.9526, lng: -75.1652 }, // Philadelphia, PA
      '78701': { lat: 30.2672, lng: -97.7431 }, // Austin, TX
      '98101': { lat: 47.6062, lng: -122.3321 }, // Seattle, WA
      '80201': { lat: 39.7392, lng: -104.9903 }, // Denver, CO
      '30301': { lat: 33.7490, lng: -84.3880 }, // Atlanta, GA
      '02101': { lat: 42.3601, lng: -71.0589 }, // Boston, MA
      '33101': { lat: 25.7617, lng: -80.1918 }, // Miami, FL
      '89101': { lat: 36.1699, lng: -115.1398 }, // Las Vegas, NV
      '97201': { lat: 45.5152, lng: -122.6784 }, // Portland, OR
      '55401': { lat: 44.9778, lng: -93.2650 }, // Minneapolis, MN
      // Minnesota specific zip codes
      '55045': { lat: 45.3838, lng: -92.8490 }, // Lindstrom, MN
      '55013': { lat: 45.3722, lng: -92.8877 }, // Chisago City, MN
      '55016': { lat: 45.0633, lng: -92.8002 }, // Forest Lake, MN
      '55101': { lat: 44.9537, lng: -93.0900 }, // Saint Paul, MN
      '55112': { lat: 45.0608, lng: -93.2869 }, // New Brighton, MN
    };

    return zipCoordinates[zipCode] || null;
  };

  // Basic city/state to coordinates lookup
  const getCoordinatesFromCityState = async (cityState) => {
    const cityCoordinates = {
      'new york, ny': { lat: 40.7128, lng: -74.0060 },
      'los angeles, ca': { lat: 34.0522, lng: -118.2437 },
      'chicago, il': { lat: 41.8781, lng: -87.6298 },
      'houston, tx': { lat: 29.7604, lng: -95.3698 },
      'phoenix, az': { lat: 33.4484, lng: -112.0740 },
      'philadelphia, pa': { lat: 39.9526, lng: -75.1652 },
      'san antonio, tx': { lat: 29.4241, lng: -98.4936 },
      'san diego, ca': { lat: 32.7157, lng: -117.1611 },
      'dallas, tx': { lat: 32.7767, lng: -96.7970 },
      'san jose, ca': { lat: 37.3382, lng: -121.8863 },
      'austin, tx': { lat: 30.2672, lng: -97.7431 },
      'jacksonville, fl': { lat: 30.3322, lng: -81.6557 },
      'fort worth, tx': { lat: 32.7555, lng: -97.3308 },
      'columbus, oh': { lat: 39.9612, lng: -82.9988 },
      'charlotte, nc': { lat: 35.2271, lng: -80.8431 },
      'san francisco, ca': { lat: 37.7749, lng: -122.4194 },
      'indianapolis, in': { lat: 39.7684, lng: -86.1581 },
      'seattle, wa': { lat: 47.6062, lng: -122.3321 },
      'denver, co': { lat: 39.7392, lng: -104.9903 },
      'boston, ma': { lat: 42.3601, lng: -71.0589 },
      'minneapolis, mn': { lat: 44.9778, lng: -93.2650 },
      'chisago city, mn': { lat: 45.3722, lng: -92.8877 },
      'lindstrom, mn': { lat: 45.3838, lng: -92.8490 },
    };

    return cityCoordinates[cityState.toLowerCase()] || null;
  };

  const loadSampleVenues = () => {
    const sampleVenues = [
      {
        id: '1',
        name: 'Chisago Lakes Community Center',
        address: '30368 Olinda Trail, Lindstrom, MN 55045',
        latitude: 45.3838,
        longitude: -92.8490,
        category: 'Recreation',
        description: 'Community center with adaptive programs and wheelchair accessible facilities.',
        accessibility_features: ['wheelchair_accessible', 'accessible_parking', 'elevator_access'],
        rating: 4.5,
        review_count: 12
      },
      {
        id: '2',
        name: 'Wild River State Park',
        address: '39797 Park Trail, Center City, MN 55012',
        latitude: 45.4191,
        longitude: -92.8766,
        category: 'Nature/Parks',
        description: 'State park with accessible trails and sensory-friendly nature programs.',
        accessibility_features: ['wheelchair_accessible', 'accessible_parking', 'sensory_friendly'],
        rating: 4.8,
        review_count: 28
      },
      {
        id: '3',
        name: 'Chisago City Library',
        address: '11830 Lake Blvd, Chisago City, MN 55013',
        latitude: 45.3722,
        longitude: -92.8877,
        category: 'Education',
        description: 'Public library with autism-friendly reading hours and adaptive technology.',
        accessibility_features: ['wheelchair_accessible', 'quiet_spaces', 'autism_friendly'],
        rating: 4.3,
        review_count: 8
      }
    ];
    setVenues(sampleVenues);
  };

  const accessibilityIcons = {
    wheelchair_accessible: '♿',
    sensory_friendly: '🔇',
    visual_accommodations: '👁️',
    hearing_accommodations: '👂',
    autism_friendly: '🧩',
    quiet_spaces: '🤫',
    accessible_parking: '🅿️',
    elevator_access: '🛗'
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Header */}
        <div className="mb-6">
          <div className="flex justify-between items-start mb-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900" data-testid="map-title">
                Nationwide Accessibility Map
              </h1>
              <p className="text-gray-600 mt-2">
                Search our nationwide database of accessible venues using zip codes or your current location
              </p>
            </div>
            <div className="flex items-center space-x-3">
              {/* View Toggle */}
              <div className="bg-gray-100 rounded-lg p-1 flex">
                <button
                  onClick={() => setViewMode('list')}
                  className={`flex items-center px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    viewMode === 'list' 
                      ? 'bg-white text-blue-600 shadow-sm' 
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                  aria-pressed={viewMode === 'list'}
                >
                  <List size={16} className="mr-1.5" />
                  List
                </button>
                <button
                  onClick={() => setViewMode('map')}
                  className={`flex items-center px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    viewMode === 'map' 
                      ? 'bg-white text-blue-600 shadow-sm' 
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                  aria-pressed={viewMode === 'map'}
                >
                  <Map size={16} className="mr-1.5" />
                  Map
                </button>
              </div>
              
              <button
                onClick={() => setShowAddVenue(true)}
                className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                data-testid="add-venue-btn"
              >
                <Plus size={20} className="mr-2" />
                Add Venue
              </button>
            </div>
          </div>

          {/* Verification Legend */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
            <div className="flex flex-wrap items-center gap-4 text-sm">
              <span className="font-medium text-blue-900">Understanding our badges:</span>
              <span className="inline-flex items-center">
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 mr-2">
                  <CheckCircle size={12} className="mr-1" /> Verified
                </span>
                <span className="text-gray-600">Officially confirmed accessibility</span>
              </span>
              <span className="inline-flex items-center">
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 mr-2">
                  <Users size={12} className="mr-1" /> Community
                </span>
                <span className="text-gray-600">Reported by parents</span>
              </span>
              <span className="inline-flex items-center">
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 mr-2">
                  <Star size={12} className="mr-1" /> Family Reviewed
                </span>
                <span className="text-gray-600">Has family reviews</span>
              </span>
            </div>
          </div>

          {/* Location Search Controls */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-3">Search Locations</h2>
            <div className="grid md:grid-cols-4 gap-4">
              {/* Location Input */}
              <div className="md:col-span-2">
                <label htmlFor="location-input" className="block text-sm font-medium text-gray-700 mb-1">
                  Enter ZIP Code or City, State
                </label>
                <input
                  type="text"
                  id="location-input"
                  value={locationSearch}
                  onChange={(e) => setLocationSearch(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleLocationSearch()}
                  placeholder="e.g., 55045, New York NY, Los Angeles CA"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  aria-describedby="location-help"
                />
                <p id="location-help" className="text-xs text-gray-500 mt-1">
                  Works with any US zip code or major city
                </p>
              </div>

              {/* Radius Selection */}
              <div>
                <label htmlFor="radius-select" className="block text-sm font-medium text-gray-700 mb-1">
                  Search Radius
                </label>
                <select
                  id="radius-select"
                  value={searchRadius}
                  onChange={(e) => setSearchRadius(parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value={5}>5 miles</option>
                  <option value={10}>10 miles</option>
                  <option value={25}>25 miles</option>
                  <option value={50}>50 miles</option>
                  <option value={100}>100 miles</option>
                </select>
              </div>

              {/* Search Buttons */}
              <div className="flex flex-col justify-end space-y-2">
                <button
                  onClick={handleLocationSearch}
                  disabled={!locationSearch.trim() || loading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px]"
                >
                  Search Area
                </button>
                <button
                  onClick={getCurrentLocation}
                  disabled={locationLoading || loading}
                  className="flex items-center justify-center px-4 py-2 bg-green-700 text-white rounded-md hover:bg-green-800 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px]"
                  aria-label="Use my current location to find nearby venues"
                >
                  {locationLoading ? (
                    <LoadingSpinner size="small" text="" />
                  ) : (
                    <>
                      <MapPin size={16} className="mr-1" />
                      Use My Location
                    </>
                  )}
                </button>
                
                <div className="flex space-x-2">
                  {canContribute ? (
                    <button
                      onClick={() => setShowSubmissionModal(true)}
                      className="flex-1 flex items-center justify-center px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 min-h-[44px]"
                      aria-label="Suggest a new venue"
                    >
                      <Plus size={16} className="mr-1" />
                      Suggest
                    </button>
                  ) : (
                    <button
                      disabled
                      className="flex-1 flex items-center justify-center px-4 py-2 bg-gray-400 text-white rounded-md cursor-not-allowed min-h-[44px]"
                      title="Create an account to suggest venues"
                    >
                      <Lock size={16} className="mr-1" />
                      Suggest
                    </button>
                  )}
                  <button
                    onClick={() => setShowFavoritesPanel(true)}
                    className="flex items-center justify-center px-4 py-2 bg-pink-600 text-white rounded-md hover:bg-pink-700 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:ring-offset-2 min-h-[44px]"
                    aria-label="View saved venues"
                  >
                    <Heart size={16} className="mr-1" />
                    Saved
                  </button>
                </div>
              </div>
            </div>

            {/* Current Location Display */}
            {currentLocation && (
              <div className="mt-3 p-2 bg-green-50 border border-green-200 rounded-md">
                <p className="text-sm text-green-800">
                  📍 Searching near your current location ({currentLocation.lat.toFixed(4)}, {currentLocation.lng.toFixed(4)})
                </p>
              </div>
            )}
          </div>
        </div>

        {loading ? (
          <div className="grid lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="aspect-video bg-gray-100 rounded-lg animate-pulse"></div>
            </div>
            <div>
              <LoadingSkeleton type="list" />
            </div>
          </div>
        ) : (
          <>
            {/* Map View */}
            {viewMode === 'map' && (
              <div className="grid lg:grid-cols-2 gap-6">
                {/* Interactive Map with Leaflet */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                  <div className="mb-3 flex justify-between items-center">
                    <h3 className="font-medium text-gray-900">Accessibility Map</h3>
                    <span className="text-sm text-gray-500">{venues.length} venues</span>
                  </div>
                  <InteractiveMap
                    venues={venues}
                    center={mapCenter}
                    selectedVenue={selectedVenue}
                    onVenueSelect={setSelectedVenue}
                    height="450px"
                  />
                  <div className="mt-3 flex items-center justify-between text-xs text-gray-500">
                    <span>Click a marker for details</span>
                    <span className="flex items-center">
                      <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse mr-1"></div>
                      Live data from OpenStreetMap
                    </span>
                  </div>
                </div>

                {/* Side Venue List (Map View) */}
                <div className="space-y-4 max-h-[550px] overflow-y-auto">
                  <h2 className="text-xl font-semibold text-gray-900 mb-4 sticky top-0 bg-gray-50 py-2">
                    Nearby Accessible Venues ({venues.length})
                  </h2>
                  {venues.slice(0, 10).map((venue) => (
                    <VenueCard 
                      key={venue.id} 
                      venue={venue} 
                      isSelected={selectedVenue?.id === venue.id}
                      onClick={() => setSelectedVenue(venue)}
                      accessibilityIcons={accessibilityIcons}
                      compact={true}
                    />
                  ))}
                  {venues.length > 10 && (
                    <p className="text-center text-gray-500 text-sm py-2">
                      Showing 10 of {venues.length} venues. Use List view to see all.
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* List View */}
            {viewMode === 'list' && (
              <div className="space-y-4">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-semibold text-gray-900">
                    Accessible Venues ({venues.length})
                  </h2>
                  <p className="text-sm text-gray-500">
                    Sorted by rating
                  </p>
                </div>
                
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {venues.map((venue) => (
                    <VenueCard 
                      key={venue.id} 
                      venue={venue} 
                      isSelected={selectedVenue?.id === venue.id}
                      onClick={() => setSelectedVenue(venue)}
                      accessibilityIcons={accessibilityIcons}
                      compact={false}
                    />
                  ))}
                </div>
                
                {venues.length === 0 && (
                  <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
                    <MapPin size={48} className="mx-auto text-gray-300 mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No venues found</h3>
                    <p className="text-gray-500">Try searching a different location or expanding your search radius.</p>
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {/* Venue Details Modal with Family Reviews */}
        {selectedVenue && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center p-6 border-b border-gray-200">
                <h2 className="text-xl font-semibold text-gray-900">{selectedVenue.name}</h2>
                <button
                  onClick={() => setSelectedVenue(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="p-6 grid lg:grid-cols-2 gap-8">
                {/* Venue Info */}
                <div>
                  <div className="flex items-center mb-4">
                    <Star size={20} className="text-yellow-400 fill-current mr-2" />
                    <span className="text-lg font-semibold">{selectedVenue.rating}</span>
                    <span className="text-gray-600 ml-2">({selectedVenue.review_count} family reviews)</span>
                  </div>
                  
                  <p className="text-gray-700 mb-4">{selectedVenue.description}</p>
                  <p className="text-gray-600 mb-4">{selectedVenue.address}</p>
                  
                  <div className="mb-6">
                    <h4 className="font-semibold text-gray-900 mb-2">Accessibility Features</h4>
                    <div className="flex flex-wrap gap-2">
                      {selectedVenue.accessibility_features.map((feature) => (
                        <span
                          key={feature}
                          className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-green-100 text-green-800"
                        >
                          {accessibilityIcons[feature]} {feature.replace(/_/g, ' ')}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="font-semibold text-blue-900 mb-2">🔄 Real-time Update</h4>
                    <p className="text-blue-800 text-sm">Last verified by family: 3 days ago</p>
                    <p className="text-blue-700 text-xs mt-1">✅ Accessible parking confirmed available</p>
                  </div>
                </div>

                {/* Family Reviews */}
                <div>
                  <h4 className="font-semibold text-gray-900 mb-4">Family Reviews & Experiences</h4>
                  <div className="space-y-4">
                    {[
                      {
                        name: 'Sarah M.',
                        rating: 5,
                        date: '2 weeks ago',
                        child_info: 'Parent of child with autism',
                        review: 'Amazing wheelchair access and the staff was incredibly patient with my son during his sensory overload. They dimmed the lights and gave him space. Highly recommend!'
                      },
                      {
                        name: 'Miguel R.',
                        rating: 4,
                        date: '1 month ago', 
                        child_info: 'Parent of twins with ADHD',
                        review: 'Great accessible features. The quiet room was perfect when the kids needed a break. Only wish they had more visual schedules posted.'
                      },
                      {
                        name: 'Jennifer L.',
                        rating: 5,
                        date: '3 weeks ago',
                        child_info: 'Parent of child with physical disability',
                        review: 'Excellent elevator access and wide doorways. Staff helped us navigate easily. Will definitely return!'
                      }
                    ].map((review, index) => (
                      <div key={index} className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <p className="font-medium text-gray-900">{review.name}</p>
                            <p className="text-xs text-blue-600">{review.child_info}</p>
                          </div>
                          <div className="flex items-center">
                            {[...Array(5)].map((_, i) => (
                              <Star key={i} size={14} className={`${i < review.rating ? 'text-yellow-400 fill-current' : 'text-gray-300'}`} />
                            ))}
                            <span className="text-xs text-gray-500 ml-2">{review.date}</span>
                          </div>
                        </div>
                        <p className="text-gray-700 text-sm">{review.review}</p>
                      </div>
                    ))}
                  </div>

                  <button className="w-full mt-4 bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700">
                    Write a Family Review
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Add Venue Modal */}
        {showAddVenue && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <h3 className="text-lg font-semibold mb-4">Add New Venue</h3>
              <p className="text-gray-600 mb-4">
                Help other families by adding a new accessible venue to our map.
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowAddVenue(false)}
                  className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">
                  Add Venue
                </button>
              </div>
            </div>
          </div>
        )}
        
        {/* Success Message */}
        {successMessage && (
          <div className="fixed top-4 right-4 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded z-50 max-w-md">
            <div className="flex items-center justify-between">
              <span>{successMessage}</span>
              <button 
                onClick={() => setSuccessMessage('')}
                className="ml-4 text-green-500 hover:text-green-700 min-w-[44px] min-h-[44px] flex items-center justify-center"
                aria-label="Dismiss message"
              >
                ×
              </button>
            </div>
          </div>
        )}

        {/* Venue Submission Modal */}
        <VenueSubmissionModal
          isOpen={showSubmissionModal}
          onClose={() => setShowSubmissionModal(false)}
          onSuccess={(message) => {
            setSuccessMessage(message);
            setTimeout(() => setSuccessMessage(''), 5000); // Hide after 5 seconds
          }}
        />
        
        {/* Favorites Panel */}
        <FavoritesPanel
          isOpen={showFavoritesPanel}
          onClose={() => setShowFavoritesPanel(false)}
          onSelectVenue={(venueId) => {
            const venue = venues.find(v => v.id === venueId);
            if (venue) setSelectedVenue(venue);
          }}
        />
        
        {/* Help Button */}
        <HelpButton pageName="map" />
      </div>
    </div>
  );
};

// VenueCard Component with verification badges and favorites
const VenueCard = ({ venue, isSelected, onClick, accessibilityIcons, compact }) => {
  // Determine verification status based on source and added_by
  // - "Verified": Official data from OpenStreetMap or system syncs
  // - "Community": User-submitted venues
  // - "Family Reviewed": Has reviews from families
  
  const isFromOfficialSource = venue.source === 'OpenStreetMap' || 
    venue.added_by?.startsWith('system_') ||
    venue.id?.includes('verified');
  
  const isUserSubmitted = venue.added_by && 
    !venue.added_by.startsWith('system_') && 
    venue.added_by !== 'admin';
  
  const hasFamilyReviews = venue.review_count > 0;
  
  // Show badges based on actual data:
  // - Verified: Only for official/system sources
  // - Community: For user-submitted venues
  // - Family Reviewed: When there are reviews
  const showVerified = isFromOfficialSource && !isUserSubmitted;
  const showCommunity = isUserSubmitted || (!isFromOfficialSource && !showVerified);
  
  // Format last updated date
  const formatLastUpdated = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Updated today';
    if (diffDays === 1) return 'Updated yesterday';
    if (diffDays < 7) return `Updated ${diffDays} days ago`;
    if (diffDays < 30) return `Updated ${Math.floor(diffDays / 7)} week${Math.floor(diffDays / 7) > 1 ? 's' : ''} ago`;
    return `Updated ${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
  };
  
  return (
    <div
      className={`bg-white rounded-lg shadow-sm border border-gray-200 ${compact ? 'p-4' : 'p-5'} cursor-pointer transition-all hover:border-blue-300 hover:shadow-md ${
        isSelected ? 'border-blue-500 ring-2 ring-blue-500' : ''
      }`}
      onClick={onClick}
      data-testid={`venue-${venue.id}`}
      role="article"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onClick(); }}
      aria-label={`${venue.name}, ${venue.address}. Click for details.`}
    >
      {/* Header with badges and favorite button */}
      <div className="flex justify-between items-start mb-2">
        <div className="flex flex-wrap gap-1.5">
          {showVerified && (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
              <CheckCircle size={10} className="mr-1" /> Verified
            </span>
          )}
          {showCommunity && (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
              <Users size={10} className="mr-1" /> Community
            </span>
          )}
          {hasFamilyReviews && (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
              <Star size={10} className="mr-1" /> Family Reviewed
            </span>
          )}
        </div>
        <FavoriteButton venueId={venue.id} venueName={venue.name} size="small" />
      </div>

      <div className="flex justify-between items-start mb-2">
        <h3 className={`${compact ? 'text-base' : 'text-lg'} font-semibold text-gray-900 line-clamp-1 flex-1`}>{venue.name}</h3>
        {venue.rating && (
          <div className="flex items-center flex-shrink-0 ml-2">
            <Star size={14} className="text-yellow-400 fill-current" />
            <span className="text-sm text-gray-600 ml-1">{venue.rating}</span>
          </div>
        )}
      </div>
      
      <p className="text-gray-600 text-sm mb-2 line-clamp-1">{venue.address}</p>
      
      {!compact && venue.description && (
        <p className="text-gray-700 text-sm mb-3 line-clamp-2">{venue.description}</p>
      )}
      
      <div className="flex flex-wrap gap-1.5">
        {venue.category && (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
            {venue.category}
          </span>
        )}
        {venue.accessibility_features?.slice(0, compact ? 2 : 3).map((feature) => (
          <span
            key={feature}
            className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800"
          >
            {accessibilityIcons[feature]} {feature.replace(/_/g, ' ')}
          </span>
        ))}
        {venue.accessibility_features?.length > (compact ? 2 : 3) && (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
            +{venue.accessibility_features.length - (compact ? 2 : 3)} more
          </span>
        )}
      </div>
      
      {/* Last updated and review info */}
      {!compact && (
        <div className="mt-3 pt-3 border-t border-gray-100 flex justify-between items-center text-xs text-gray-500">
          <span className="flex items-center">
            <Clock size={12} className="mr-1" />
            {formatLastUpdated(venue.last_updated) || 'Recently added'}
          </span>
          {hasFamilyReviews && (
            <span>{venue.review_count} family {venue.review_count === 1 ? 'review' : 'reviews'}</span>
          )}
        </div>
      )}
    </div>
  );
};

export default MapPage;